import React from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Divider, 
  Button, 
  FormControlLabel, 
  Switch, 
  TableContainer, 
  Table, 
  TableBody, 
  TableRow, 
  TableCell 
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { themeColors } from '../common/constants';

const SettingsTab = () => {
  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              System Configuration
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                General Settings
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Default Date Range</InputLabel>
                <Select
                  value="last30"
                  label="Default Date Range"
                >
                  <MenuItem value="last7">Last 7 Days</MenuItem>
                  <MenuItem value="last30">Last 30 Days</MenuItem>
                  <MenuItem value="last90">Last 90 Days</MenuItem>
                  <MenuItem value="thisYear">This Year</MenuItem>
                  <MenuItem value="allTime">All Time</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Auto-refresh Dashboard</InputLabel>
                <Select
                  value="15min"
                  label="Auto-refresh Dashboard"
                >
                  <MenuItem value="never">Never</MenuItem>
                  <MenuItem value="5min">Every 5 minutes</MenuItem>
                  <MenuItem value="15min">Every 15 minutes</MenuItem>
                  <MenuItem value="30min">Every 30 minutes</MenuItem>
                  <MenuItem value="1hour">Every hour</MenuItem>
                </Select>
              </FormControl>
              
              <Divider sx={{ mb: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Email Notifications
              </Typography>
              
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Switch defaultChecked />}
                      label="Send daily summary reports"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Switch defaultChecked />}
                      label="Notify on low satisfaction ratings"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Switch />}
                      label="Send weekly performance analytics"
                    />
                  </Grid>
                </Grid>
              </FormControl>
              
              <Button 
                variant="contained" 
                sx={{ 
                  bgcolor: themeColors.primary,
                  '&:hover': {
                    bgcolor: themeColors.primaryLight
                  }
                }}
              >
                Save Settings
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              Data Management
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Data Retention
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Retain Response Data For</InputLabel>
                <Select
                  value="3years"
                  label="Retain Response Data For"
                >
                  <MenuItem value="1year">1 Year</MenuItem>
                  <MenuItem value="2years">2 Years</MenuItem>
                  <MenuItem value="3years">3 Years</MenuItem>
                  <MenuItem value="5years">5 Years</MenuItem>
                  <MenuItem value="indefinite">Indefinitely</MenuItem>
                </Select>
              </FormControl>
              
              <Typography variant="subtitle1" gutterBottom>
                Export Options
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Button 
                    fullWidth 
                    variant="outlined" 
                    startIcon={<DownloadIcon />}
                    sx={{ 
                      borderColor: themeColors.primary, 
                      color: themeColors.primary,
                      '&:hover': {
                        borderColor: themeColors.primaryDark,
                        backgroundColor: 'rgba(128, 0, 0, 0.04)'
                      }
                    }}
                  >
                    Export All Data (CSV)
                  </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Button 
                    fullWidth 
                    variant="outlined" 
                    startIcon={<DownloadIcon />}
                    sx={{ 
                      borderColor: themeColors.primary, 
                      color: themeColors.primary,
                      '&:hover': {
                        borderColor: themeColors.primaryDark,
                        backgroundColor: 'rgba(128, 0, 0, 0.04)'
                      }
                    }}
                  >
                    Export Analytics (PDF)
                  </Button>
                </Grid>
              </Grid>
              
              <Divider sx={{ mb: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#F44336' }}>
                Danger Zone
              </Typography>
              
              <Button 
                variant="outlined"
                sx={{ 
                  borderColor: '#F44336', 
                  color: '#F44336',
                  '&:hover': {
                    borderColor: '#D32F2F',
                    backgroundColor: 'rgba(244, 67, 54, 0.04)'
                  }
                }}
              >
                Reset Dashboard Data
              </Button>
            </Box>
          </Paper>
          
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
              System Information
            </Typography>
            
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Dashboard Version</TableCell>
                    <TableCell>v2.5.1</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Last Updated</TableCell>
                    <TableCell>March 15, 2025</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Database Status</TableCell>
                    <TableCell><span style={{ color: '#4CAF50' }}>●</span> Connected</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Total Responses</TableCell>
                    <TableCell>324</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>Storage Used</TableCell>
                    <TableCell>2.3 GB / 10 GB</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SettingsTab;